import { produce, SetStoreFunction } from 'solid-js/store';
import {
  LoadableData,
  LoadableItem,
  LoadableItems,
  LoadState,
} from './data-loading.types';

/**
 * Creates a function that updates a single item's loadState within a named store slice.
 * Uses `produce` for immutable-style nested updates in Solid stores.
 *
 * The returned setter is typed against the concrete `TState`, so it plugs
 * directly into `ProviderHandlerConfig<_, _, TState>.setLoadState` without a
 * cast at the call site.
 *
 * @param sliceKey The key of the slice in the store (e.g. 'pointCloud', 'trajectory')
 * @returns A setter function compatible with ProviderHandlerConfig.setLoadState
 */
export function setItemLoadState<TState extends LoadableData>(
  sliceKey: keyof TState & string
): (
  setDataState: SetStoreFunction<TState>,
  itemId: string,
  loadState: LoadState
) => void {
  return (setDataState, itemId, loadState) => {
    setDataState(
      produce((state: TState) => {
        const slice: LoadableItems<LoadableItem> = state[sliceKey];
        if (slice.state !== LoadState.LoadComplete) return;

        const item = slice.items[itemId];
        if (!item) return;

        item.loadState = loadState;
      })
    );
  };
}
